/**
 * WhatsApp settings — Meta Cloud API integration.
 *
 * Shows a status panel + the 6-step setup guide + a settings form (token,
 * phone_number_id, business_account_id, template names) and a "Send test"
 * button.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const TEMPLATE_KEYS = ["fee_reminder", "fee_receipt", "marksheet_published", "leave_decision", "absent_alert"];

function SetupGuide() {
  return (
    <div className="vs-card p-5 space-y-3" data-testid="wa-setup-guide">
      <div className="font-medium">6-step setup guide — Meta WhatsApp Cloud API</div>
      <ol className="text-sm space-y-2 list-decimal pl-5">
        <li>
          <strong>Create / sign in to Meta Business Manager</strong> →{" "}
          <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-[var(--vs-secondary)] underline">business.facebook.com</a>.
          Add your trust as a Business.
        </li>
        <li>
          Open the <strong>Meta App Dashboard</strong> →{" "}
          <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-[var(--vs-secondary)] underline">developers.facebook.com/apps</a>.
          Click <em>Create app</em> → choose <em>Business</em> → add the <em>WhatsApp</em> product.
        </li>
        <li>
          From the <em>WhatsApp → Getting Started</em> panel, copy the test
          <strong> phone_number_id</strong> and the <strong>business_account_id</strong>.
          Add and verify a real phone number ("Add phone number") for production.
        </li>
        <li>
          Generate a <strong>permanent System User access token</strong>:
          Business Settings → Users → System Users → New → Add Assets =
          WhatsApp account (Full Control). Copy the token (starts with <code>EAAB…</code>).
        </li>
        <li>
          Create <strong>message templates</strong> in WhatsApp Manager →
          <em>Message templates</em>. Templates we use:
          <ul className="list-disc pl-5 text-xs mt-1">
            <li><code>fee_reminder</code> — 4 body placeholders: parent_name, student_name, amount, due_date</li>
            <li><code>fee_receipt</code> — 4 body placeholders: parent_name, student_name, amount, receipt_no</li>
            <li><code>marksheet_published</code> — 3 body placeholders: parent_name, student_name, exam_percentage</li>
            <li><code>leave_decision</code> — 4 body placeholders: parent_name, student_name, decision, date_range</li>
          </ul>
          Wait for each template to be approved (usually &lt; 1 hour).
        </li>
        <li>
          Paste the token / phone_number_id / business_account_id / template
          names into the form below and hit <strong>Save</strong>. Use
          <em> Send test</em> to verify (free-text only works within the 24 h
          customer-service window — template messages work anytime).
        </li>
      </ol>
      <div className="text-xs text-[var(--vs-text-secondary)] bg-amber-50 border border-amber-200 rounded p-2">
        💡 <strong>India cost guidance:</strong> ~ ₹ 0.30 per <em>utility</em> message and ~ ₹ 0.78 per <em>marketing</em> message
        (Meta tariff, May 2026). Auth + transaction templates such as fee_receipt usually fall under <em>utility</em>.
      </div>
    </div>
  );
}

export default function WhatsAppSettings() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({
    provider: "meta_cloud", access_token: "", phone_number_id: "",
    business_account_id: "", default_template_lang: "en_US", templates: {},
    webhook_verify_token: "",
  });
  const [test, setTest] = useState({ to: "", body: "", template: "", template_params: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/whatsapp/config");
    setCfg(r.data);
    setDraft((d) => ({
      ...d,
      provider: r.data.provider || "meta_cloud",
      phone_number_id: r.data.phone_number_id || "",
      business_account_id: r.data.business_account_id || "",
      default_template_lang: r.data.default_template_lang || "en_US",
      templates: r.data.templates || {},
      access_token: "",
      webhook_verify_token: "",
    }));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...draft };
      // Only send access_token if user typed a new one.
      if (!payload.access_token) delete payload.access_token;
      if (!payload.webhook_verify_token) delete payload.webhook_verify_token;
      await api.post("/admin/whatsapp/config", payload);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!test.to) { toast.error("Enter a recipient phone number (E.164 or 10-digit Indian)"); return; }
    setBusy(true);
    try {
      const body = { to: test.to };
      if (test.template) {
        body.template = test.template;
        body.template_params = test.template_params.split("|").map((s) => s.trim()).filter(Boolean);
      } else {
        body.body = test.body || "Hello from Vidya Seva — this is a test WhatsApp message.";
      }
      const r = await api.post("/admin/whatsapp/test", body);
      if (r.data.delivery_error) toast.error(`Delivery error: ${r.data.delivery_error}`);
      else toast.success(`Sent · delivery=${r.data.delivery}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (!cfg) return <DashboardLayout><div className="p-8 text-sm">Loading…</div></DashboardLayout>;

  const statusBadge = cfg.provider === "meta_cloud" && cfg.access_token_set
    ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">LIVE</span>
    : <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">DRY-RUN</span>;

  return (
    <DashboardLayout>
      <div className="vs-section flex items-center gap-3">
        <h1 className="text-2xl font-semibold">WhatsApp (Meta Cloud API)</h1>
        {statusBadge}
      </div>

      <SetupGuide />

      <div className="vs-card p-5 mt-4 space-y-3">
        <div className="font-medium">Settings</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Access token <span className="text-[10px] text-[var(--vs-text-secondary)]">(redacted: {cfg.access_token || "—"})</span></Label>
            <Input data-testid="wa-token" placeholder="EAAB…" value={draft.access_token} onChange={(e) => setDraft({ ...draft, access_token: e.target.value })} className="bg-white font-mono" />
          </div>
          <div>
            <Label>Phone number ID</Label>
            <Input data-testid="wa-phone-id" value={draft.phone_number_id} onChange={(e) => setDraft({ ...draft, phone_number_id: e.target.value })} className="bg-white font-mono" />
          </div>
          <div>
            <Label>Business account ID</Label>
            <Input data-testid="wa-baid" value={draft.business_account_id} onChange={(e) => setDraft({ ...draft, business_account_id: e.target.value })} className="bg-white font-mono" />
          </div>
          <div>
            <Label>Default template language</Label>
            <Input data-testid="wa-lang" placeholder="en_US" value={draft.default_template_lang} onChange={(e) => setDraft({ ...draft, default_template_lang: e.target.value })} className="bg-white font-mono" />
          </div>
        </div>
        <div className="bg-[var(--vs-bg-subtle)] border border-[var(--vs-border)]/40 rounded p-3 space-y-2">
          <div className="font-medium text-xs uppercase tracking-widest text-[var(--vs-text-secondary)]">Webhook</div>
          <div className="text-xs">
            Paste the following URL into <em>Meta App Dashboard → WhatsApp → Configuration → Webhooks → Callback URL</em>:
            <div className="mt-1 p-2 bg-white border border-[var(--vs-border)]/40 rounded font-mono text-xs break-all" data-testid="wa-webhook-url">
              {(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/whatsapp/webhook
            </div>
          </div>
          <div>
            <Label>Verify token <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(any random string — also paste in Meta's Webhook Verify Token field)</span></Label>
            <Input data-testid="wa-verify-token" placeholder={cfg.webhook_verify_token_set ? "(already set — type to replace)" : "e.g. vsms-webhook-9b4c"}
              value={draft.webhook_verify_token} onChange={(e) => setDraft({ ...draft, webhook_verify_token: e.target.value })} className="bg-white font-mono" />
          </div>
          <div className="text-xs text-[var(--vs-text-secondary)]">
            After saving, in Meta's webhook config click <strong>Verify and save</strong>. Meta will GET our webhook with <code>hub.verify_token</code>; we echo back <code>hub.challenge</code> only if it matches.
            Subscribe to the fields <code>messages</code> and <code>message_status</code>.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TEMPLATE_KEYS.map((k) => (
            <div key={k}>
              <Label>Template — <span className="font-mono text-xs">{k}</span></Label>
              <Input data-testid={`wa-tpl-${k}`} value={draft.templates?.[k] || ""}
                onChange={(e) => setDraft({ ...draft, templates: { ...(draft.templates || {}), [k]: e.target.value } })}
                className="bg-white font-mono" placeholder={`approved template name for ${k}`} />
            </div>
          ))}
        </div>
        <Button data-testid="wa-save" onClick={save} disabled={busy}>Save</Button>
      </div>

      <div className="vs-card p-5 mt-4 space-y-3">
        <div className="font-medium">Send test message</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label>To (E.164 or 10-digit IN)</Label>
            <Input data-testid="wa-test-to" placeholder="+91 90000 99911" value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} className="bg-white font-mono" />
          </div>
          <div className="md:col-span-1">
            <Label>Template (optional)</Label>
            <Input data-testid="wa-test-template" placeholder="e.g. vidya_fee_reminder" value={test.template} onChange={(e) => setTest({ ...test, template: e.target.value })} className="bg-white font-mono" />
          </div>
          <div className="md:col-span-1">
            <Label>Template params (pipe-separated)</Label>
            <Input data-testid="wa-test-params" placeholder="Parent | Aarav | ₹ 5,000 | 25-May-2026" value={test.template_params} onChange={(e) => setTest({ ...test, template_params: e.target.value })} className="bg-white" />
          </div>
          <div className="md:col-span-3">
            <Label>Free-text body (only used if no template)</Label>
            <Input data-testid="wa-test-body" value={test.body} onChange={(e) => setTest({ ...test, body: e.target.value })} className="bg-white" />
          </div>
        </div>
        <Button data-testid="wa-test-send" onClick={sendTest} disabled={busy}>Send test</Button>
      </div>
    </DashboardLayout>
  );
}
